import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Divider,
  alpha,
  LinearProgress,
  Stack,
  Alert,
  Chip,
  Box,
  CircularProgress,
  Paper,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import {
  Save,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Refresh,
  Add,
  Edit,
  Person,
  Business,
} from "@mui/icons-material";
import MDButton from "shared/components/MDButton";
import MDBox from "shared/components/MDBox";

const filtrarUsuarios = createFilterOptions({
  stringify: (usuario) =>
    [usuario.username, usuario.name, usuario.role?.name].filter(Boolean).join(" "),
});

const MODULOS = [
  {
    id: "propiedades",
    nombre: "Propiedades",
    icono: "🏢",
    acciones: ["ver", "crear", "editar", "activar", "desactivar"],
    esGlobal: true,
  },
  {
    id: "usuarios",
    nombre: "Usuarios",
    icono: "👤",
    acciones: ["ver", "crear", "editar", "eliminar"],
    esGlobal: false,
  },
  {
    id: "registros",
    nombre: "Registros",
    icono: "🚗",
    acciones: ["ver", "entrada", "salida"],
    esGlobal: false,
  },
];

const ACCIONES_LABELS = {
  ver: "Ver",
  crear: "Crear",
  editar: "Editar",
  eliminar: "Eliminar",
  activar: "Activar",
  desactivar: "Desactivar",
  entrada: "Entrada",
  salida: "Salida",
};

const COLORS = {
  primary: "#0b4f6c",
  success: "#27ae60",
  error: "#e74c3c",
  warning: "#f39c12",
  info: "#2980b9",
};

const ModalPermisos = ({
  open,
  onClose,
  onSave,
  onLoadPermisos,
  usuarios = [],
  propiedades = [],
  permisosData = [],
  usuarioId = null,
  loading = false,
  isSubmitting = false,
  titulo = "Asignar Permisos",
}) => {
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedPropiedades, setSelectedPropiedades] = useState([]);
  const [permisosSeleccionados, setPermisosSeleccionados] = useState({});
  const [cargandoPermisos, setCargandoPermisos] = useState(false);
  const [error, setError] = useState(null);
  const [permisosCargados, setPermisosCargados] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");

  // 🔥 Permisos GLOBALES de propiedades
  const [permisosGlobalesPropiedades, setPermisosGlobalesPropiedades] = useState({
    ver: false,
    crear: false,
    editar: false,
    activar: false,
    desactivar: false,
  });

  // 🔥 ID del permiso global de propiedades (para actualizar en lugar de crear)
  const [idPermisoGlobalPropiedades, setIdPermisoGlobalPropiedades] = useState(null);

  // 🔥 IDs de permisos antiguos de propiedades (con co_prop_id !== 0) que deben eliminarse
  const [idsPermisosPropiedadesAntiguos, setIdsPermisosPropiedadesAntiguos] = useState([]);

  useEffect(() => {
    if (open) {
      setError(null);
      setPermisosCargados(false);
      setSubmitStatus(null);
      setStatusMessage("");

      if (usuarioId) {
        setSelectedUser(String(usuarioId));
        cargarPermisosUsuario(usuarioId);
      } else {
        setSelectedUser("");
        setSelectedPropiedades([]);
        setPermisosSeleccionados({});
        setPermisosGlobalesPropiedades({
          ver: false,
          crear: false,
          editar: false,
          activar: false,
          desactivar: false,
        });
        setIdPermisoGlobalPropiedades(null);
        setIdsPermisosPropiedadesAntiguos([]);
      }
    }
  }, [open, usuarioId]);

  // ============================================================
  // CARGAR PERMISOS DEL USUARIO
  // ============================================================
  const cargarPermisosUsuario = async (userId) => {
    if (!userId || !onLoadPermisos) return;
    setCargandoPermisos(true);
    setError(null);
    try {
      const result = await onLoadPermisos(userId);

      if (result && typeof result === "object" && Object.keys(result).length > 0) {
        const propIds = new Set();
        const newPermisos = {};
        let globalPropiedades = {
          ver: false,
          crear: false,
          editar: false,
          activar: false,
          desactivar: false,
        };
        let idGlobal = null;
        const idsAntiguos = [];

        Object.keys(result).forEach((key) => {
          const [coPropId, table] = key.split("_");
          const rol = result[key].rol || result[key];
          const id = result[key].id;

          if (table === "propiedades") {
            // 🔥 Si el co_prop_id es 0, es el permiso GLOBAL
            if (Number(coPropId) === 0) {
              globalPropiedades = { ...rol };
              idGlobal = id;
            } else {
              // 🔥 Si es un permiso antiguo de propiedades (co_prop_id !== 0), guardarlo para eliminarlo
              idsAntiguos.push({ id, co_prop_id: Number(coPropId), table });
            }
          } else {
            // Módulos por comercio (usuarios, registros)
            newPermisos[key] = rol;
            const tieneAlgunPermisoActivo = Object.values(rol).some((v) => v === true);
            if (tieneAlgunPermisoActivo) {
              propIds.add(Number(coPropId));
            }
          }
        });

        setPermisosSeleccionados(newPermisos);
        setPermisosGlobalesPropiedades(globalPropiedades);
        setIdPermisoGlobalPropiedades(idGlobal);
        setIdsPermisosPropiedadesAntiguos(idsAntiguos);
        setSelectedPropiedades(Array.from(propIds));
        setPermisosCargados(true);
      } else {
        setPermisosSeleccionados({});
        setPermisosGlobalesPropiedades({
          ver: false,
          crear: false,
          editar: false,
          activar: false,
          desactivar: false,
        });
        setIdPermisoGlobalPropiedades(null);
        setIdsPermisosPropiedadesAntiguos([]);
        setSelectedPropiedades([]);
        setPermisosCargados(true);
      }
    } catch (err) {
      console.error("Error al cargar permisos:", err);
      setError("Error al cargar permisos del usuario");
      setPermisosSeleccionados({});
      setPermisosGlobalesPropiedades({
        ver: false,
        crear: false,
        editar: false,
        activar: false,
        desactivar: false,
      });
      setIdPermisoGlobalPropiedades(null);
      setIdsPermisosPropiedadesAntiguos([]);
      setSelectedPropiedades([]);
    } finally {
      setCargandoPermisos(false);
    }
  };

  useEffect(() => {
    if (open && selectedUser && !usuarioId && !permisosCargados) {
      cargarPermisosUsuario(Number(selectedUser));
    }
  }, [selectedUser, open, usuarioId, permisosCargados]);

  useEffect(() => {
    if (open && !usuarioId && selectedUser) {
      setPermisosCargados(false);
    }
  }, [selectedUser, open, usuarioId]);

  const getPropiedadesFiltradas = () => {
    if (!selectedUser) return [];
    if (!permisosCargados || Object.keys(permisosSeleccionados).length === 0) {
      return propiedades;
    }
    const propIdsConPermisos = new Set();
    Object.keys(permisosSeleccionados).forEach((key) => {
      const [coPropId] = key.split("_");
      propIdsConPermisos.add(Number(coPropId));
    });
    return propiedades.filter((prop) => propIdsConPermisos.has(prop.id));
  };

  const togglePropiedad = (propId) => {
    setSelectedPropiedades((prev) => {
      if (prev.includes(propId)) {
        return prev.filter((id) => id !== propId);
      } else {
        const newPermisos = { ...permisosSeleccionados };
        MODULOS.filter((m) => !m.esGlobal).forEach((modulo) => {
          const key = `${propId}_${modulo.id}`;
          if (!newPermisos[key]) {
            const accionesDefault = {};
            modulo.acciones.forEach((accion) => {
              accionesDefault[accion] = false;
            });
            newPermisos[key] = accionesDefault;
          }
        });
        setPermisosSeleccionados(newPermisos);
        return [...prev, propId];
      }
    });
  };

  const toggleAccionGlobal = (accion) => {
    setPermisosGlobalesPropiedades((prev) => {
      if (accion === "ver" && prev.ver) {
        return {
          ver: false,
          crear: false,
          editar: false,
          activar: false,
          desactivar: false,
        };
      }
      if (accion !== "ver" && !prev.ver) {
        return { ...prev, ver: true, [accion]: !prev[accion] };
      }
      return { ...prev, [accion]: !prev[accion] };
    });
  };

  const toggleModuloCompletoGlobal = () => {
    const modulo = MODULOS.find((m) => m.id === "propiedades");
    const allActive = modulo.acciones.every((a) => permisosGlobalesPropiedades[a]);
    const newAcciones = {};
    modulo.acciones.forEach((a) => {
      newAcciones[a] = !allActive;
    });
    setPermisosGlobalesPropiedades(newAcciones);
  };

  const isModuloCompletoGlobal = () => {
    const modulo = MODULOS.find((m) => m.id === "propiedades");
    return modulo.acciones.every((a) => permisosGlobalesPropiedades[a]);
  };

  const toggleAccion = (propId, moduloId, accion) => {
    const key = `${propId}_${moduloId}`;
    setPermisosSeleccionados((prev) => {
      const current = prev[key] || {};
      if (accion === "ver" && current[accion]) {
        const newAcciones = { ...current };
        MODULOS.find((m) => m.id === moduloId)?.acciones.forEach((a) => {
          newAcciones[a] = false;
        });
        return { ...prev, [key]: newAcciones };
      }
      if (accion !== "ver" && !current.ver) {
        return {
          ...prev,
          [key]: { ...current, ver: true, [accion]: !current[accion] },
        };
      }
      return {
        ...prev,
        [key]: { ...current, [accion]: !current[accion] },
      };
    });
  };

  const toggleModuloCompleto = (propId, moduloId) => {
    const key = `${propId}_${moduloId}`;
    const modulo = MODULOS.find((m) => m.id === moduloId);
    setPermisosSeleccionados((prev) => {
      const current = prev[key] || {};
      const allActive = modulo?.acciones.every((a) => current[a]) || false;
      const newAcciones = {};
      modulo?.acciones.forEach((a) => {
        newAcciones[a] = !allActive;
      });
      return { ...prev, [key]: newAcciones };
    });
  };

  const isModuloCompleto = (propId, moduloId) => {
    const key = `${propId}_${moduloId}`;
    const current = permisosSeleccionados[key] || {};
    const modulo = MODULOS.find((m) => m.id === moduloId);
    return modulo?.acciones.every((a) => current[a]) || false;
  };

  const contarPermisosActivos = (rol) => {
    if (!rol || typeof rol !== "object") return 0;
    return Object.values(rol).filter((v) => v === true).length;
  };

  const hasPermisosSeleccionados = () => {
    const tieneGlobales = Object.values(permisosGlobalesPropiedades).some((v) => v === true);
    if (tieneGlobales) return true;
    return Object.keys(permisosSeleccionados).some((key) => {
      const [coPropId] = key.split("_");
      if (!selectedPropiedades.includes(Number(coPropId))) return false;
      const rol = permisosSeleccionados[key];
      return Object.values(rol).some((v) => v === true);
    });
  };

  const calcularProgreso = () => {
    let totalAcciones = 0;
    let activas = 0;
    const moduloProp = MODULOS.find((m) => m.id === "propiedades");
    totalAcciones += moduloProp.acciones.length;
    activas += Object.values(permisosGlobalesPropiedades).filter((v) => v === true).length;

    selectedPropiedades.forEach((propId) => {
      MODULOS.filter((m) => !m.esGlobal).forEach((modulo) => {
        const key = `${propId}_${modulo.id}`;
        const acciones = permisosSeleccionados[key] || {};
        totalAcciones += modulo.acciones.length;
        activas += Object.values(acciones).filter((v) => v === true).length;
      });
    });
    return totalAcciones > 0 ? Math.round((activas / totalAcciones) * 100) : 0;
  };

  // ============================================================
  // GUARDAR PERMISOS
  // ============================================================
  const handleSave = async () => {
    if (!selectedUser) {
      setError("Seleccione un usuario");
      return;
    }

    const permisosDataToSend = [];

    // 🔥 1. Módulo de PROPIEDADES (GLOBAL) - SIEMPRE
    // Si ya existe un permiso global (id), se envía con ese id para actualizar
    // Si no existe, se crea con co_prop_id: 0
    permisosDataToSend.push({
      id: idPermisoGlobalPropiedades || undefined,
      co_prop_id: 0,
      table: "propiedades",
      rol: permisosGlobalesPropiedades,
    });

    // 🔥 2. Módulos por comercio (usuarios y registros)
    selectedPropiedades.forEach((propId) => {
      MODULOS.filter((m) => !m.esGlobal).forEach((modulo) => {
        const key = `${propId}_${modulo.id}`;
        const rol = permisosSeleccionados[key] || {
          ...modulo.acciones.reduce((acc, a) => ({ ...acc, [a]: false }), {}),
        };
        permisosDataToSend.push({
          co_prop_id: Number(propId),
          table: modulo.id,
          rol: rol,
        });
      });
    });

    // 🔥 3. IDs de permisos antiguos de propiedades que deben eliminarse
    const permisosAEliminar = idsPermisosPropiedadesAntiguos;

    if (permisosDataToSend.length === 0) {
      setError("Seleccione al menos un permiso");
      return;
    }

    setError(null);
    setSubmitStatus(null);
    setStatusMessage("Guardando permisos...");
    setCargandoPermisos(true);

    try {
      const result = await onSave(Number(selectedUser), permisosDataToSend, permisosAEliminar);
      if (result?.success) {
        setSubmitStatus("success");
        setStatusMessage(result.message || "Permisos guardados exitosamente");
        setTimeout(() => {
          onClose(true);
        }, 1500);
      } else {
        setSubmitStatus("error");
        setStatusMessage(result?.error || "Error al guardar permisos");
      }
    } catch (err) {
      setSubmitStatus("error");
      setStatusMessage("Error al guardar permisos");
    } finally {
      setCargandoPermisos(false);
    }
  };

  const handleClose = () => {
    onClose(false);
  };

  const getUsuarioNombre = (id) => {
    const user = usuarios.find((u) => u.id === id);
    return user?.username || user?.name || `Usuario ${id}`;
  };

  const getPropiedadNombre = (id) => {
    const prop = propiedades.find((p) => p.id === id);
    return prop?.name || `Propiedad ${id}`;
  };

  const isLoading = loading || cargandoPermisos;

  const getPermisosStats = () => {
    let total = 0;
    let activos = 0;
    const moduloProp = MODULOS.find((m) => m.id === "propiedades");
    total += moduloProp.acciones.length;
    activos += Object.values(permisosGlobalesPropiedades).filter((v) => v === true).length;

    selectedPropiedades.forEach((propId) => {
      MODULOS.filter((m) => !m.esGlobal).forEach((modulo) => {
        const key = `${propId}_${modulo.id}`;
        const acciones = permisosSeleccionados[key] || {};
        total += modulo.acciones.length;
        activos += Object.values(acciones).filter((v) => v === true).length;
      });
    });
    return { total, activos };
  };

  const stats = getPermisosStats();
  const progreso = calcularProgreso();
  const tienePermisos = hasPermisosSeleccionados();
  const propiedadesFiltradas = getPropiedadesFiltradas();

  const renderModuloPropiedadesGlobal = () => {
    const modulo = MODULOS.find((m) => m.id === "propiedades");
    const acciones = permisosGlobalesPropiedades;
    const hasPermisosModulo = Object.values(acciones).some((v) => v);
    const isComplete = isModuloCompletoGlobal();
    const accionesActivas = Object.values(acciones).filter((v) => v).length;
    const totalAcciones = modulo.acciones.length;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          backgroundColor: "#ffffff",
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
          <Business sx={{ mr: 1, verticalAlign: "middle", fontSize: 16 }} />
          Permisos Globales de Propiedades
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Estos permisos aplican a la gestión de propiedades (comercios) en general.
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: "8px",
            border: "1px solid",
            borderColor: hasPermisosModulo ? "#c8e6c9" : "#e0e0e0",
            backgroundColor: hasPermisosModulo ? "#f1f8f1" : "#ffffff",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="caption" fontWeight="bold">
              {modulo.icono} {modulo.nombre}
            </Typography>
            <Tooltip title={isComplete ? "Desactivar todos" : "Activar todos"}>
              <IconButton
                size="small"
                color={isComplete ? "primary" : "default"}
                onClick={toggleModuloCompletoGlobal}
              >
                <CheckCircle fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box display="flex" flexDirection="column" gap={0.25}>
            {modulo.acciones.map((accion) => (
              <FormControlLabel
                key={accion}
                control={
                  <Checkbox
                    size="small"
                    checked={acciones[accion] || false}
                    onChange={() => toggleAccionGlobal(accion)}
                    disabled={accion !== "ver" && !acciones.ver}
                    sx={{ "&.Mui-checked": { color: COLORS.primary } }}
                  />
                }
                label={<Typography variant="caption">{ACCIONES_LABELS[accion]}</Typography>}
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.75rem" } }}
              />
            ))}
          </Box>

          <Box mt={1}>
            <Chip
              label={`${accionesActivas}/${totalAcciones}`}
              size="small"
              color={hasPermisosModulo ? "success" : "default"}
              sx={{ height: "18px", "& .MuiChip-label": { fontSize: "0.55rem" } }}
            />
          </Box>
        </Paper>
      </Paper>
    );
  };

  const renderContent = () => {
    return (
      <Stack spacing={3}>
        {submitStatus && (
          <Alert
            severity={submitStatus === "success" ? "success" : "error"}
            sx={{ borderRadius: "8px" }}
          >
            <Typography variant="body2" fontWeight="medium">
              {statusMessage}
            </Typography>
          </Alert>
        )}
        {error && !submitStatus && (
          <Alert severity="error" sx={{ borderRadius: "8px" }}>
            {error}
          </Alert>
        )}

        <MDBox
          sx={{
            p: 3,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Autocomplete
            fullWidth
            options={usuarios}
            value={usuarios.find((usuario) => String(usuario.id) === String(selectedUser)) || null}
            getOptionLabel={(usuario) => usuario.username || usuario.name || String(usuario.id)}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            filterOptions={filtrarUsuarios}
            onChange={(_, usuario) => {
              setSelectedUser(usuario ? String(usuario.id) : "");
              setPermisosCargados(false);
              setError(null);
              setSubmitStatus(null);
            }}
            disabled={isLoading || !!usuarioId}
            noOptionsText="No se encontraron usuarios"
            openText="Mostrar usuarios"
            closeText="Cerrar lista"
            clearText="Limpiar selección"
            ListboxProps={{
              style: { maxHeight: "min(280px, 40vh)", overflowY: "auto" },
            }}
            sx={{ "& .MuiAutocomplete-inputRoot": { minHeight: "56px" } }}
            renderInput={(params) => (
              // MUI proporciona las referencias y atributos de accesibilidad del campo.
              // eslint-disable-next-line react/jsx-props-no-spreading
              <TextField
                {...params}
                label="Seleccionar Usuario"
                placeholder="Buscar usuario o rol"
              />
            )}
            renderOption={(props, usuario) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <Box component="li" {...props} key={usuario.id} sx={{ gap: 1, minHeight: "44px" }}>
                <Person fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  noWrap
                  title={usuario.username || usuario.name}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  {usuario.username || usuario.name || String(usuario.id)}
                </Typography>
                <Chip
                  label={usuario.role?.name || "Sin rol"}
                  title={usuario.role?.name || "Sin rol"}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: "24px",
                    maxWidth: "45%",
                    flexShrink: 0,
                    "& .MuiChip-label": { fontSize: "0.7rem" },
                  }}
                />
              </Box>
            )}
          />
        </MDBox>

        {selectedUser && (
          <>
            {renderModuloPropiedadesGlobal()}

            <MDBox
              sx={{
                p: 3,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Propiedades Disponibles (Usuarios y Registros)
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {propiedadesFiltradas.map((prop) => (
                  <Chip
                    key={prop.id}
                    label={prop.name}
                    onClick={() => togglePropiedad(prop.id)}
                    color={selectedPropiedades.includes(prop.id) ? "primary" : "default"}
                    icon={<Business />}
                    variant={selectedPropiedades.includes(prop.id) ? "filled" : "outlined"}
                    sx={{
                      cursor: "pointer",
                      "&:hover": { transform: "scale(1.02)", transition: "transform 0.2s" },
                    }}
                  />
                ))}
              </Box>
              {propiedadesFiltradas.length === 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  No hay propiedades con permisos asignados para este usuario.
                </Typography>
              )}
            </MDBox>

            {isLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : selectedPropiedades.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: "8px" }}>
                {permisosCargados && Object.keys(permisosSeleccionados).length === 0
                  ? "El usuario no tiene permisos asignados en usuarios o registros. Seleccione una propiedad para asignar permisos."
                  : "Seleccione al menos una propiedad para asignar permisos de usuarios o registros"}
              </Alert>
            ) : (
              <Stack spacing={2}>
                {selectedPropiedades.map((propId) => (
                  <Paper
                    key={propId}
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      border: "1px solid #e0e0e0",
                      overflow: "hidden",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                      <Business sx={{ mr: 1, verticalAlign: "middle", fontSize: 16 }} />
                      {getPropiedadNombre(propId)}
                    </Typography>

                    <Grid container spacing={2}>
                      {MODULOS.filter((m) => !m.esGlobal).map((modulo) => {
                        const key = `${propId}_${modulo.id}`;
                        const acciones = permisosSeleccionados[key] || {};
                        const hasPermisosModulo = Object.values(acciones).some((v) => v);
                        const isComplete = isModuloCompleto(propId, modulo.id);
                        const accionesActivas = Object.values(acciones).filter((v) => v).length;
                        const totalAcciones = modulo.acciones.length;

                        return (
                          <Grid item xs={12} md={6} key={modulo.id}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 1.5,
                                borderRadius: "8px",
                                border: "1px solid",
                                borderColor: hasPermisosModulo ? "#c8e6c9" : "#e0e0e0",
                                backgroundColor: hasPermisosModulo ? "#f1f8f1" : "#ffffff",
                                transition: "all 0.2s",
                                "&:hover": {
                                  borderColor: hasPermisosModulo ? "#a5d6a7" : "#bdbdbd",
                                },
                              }}
                            >
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={1}
                              >
                                <Typography variant="caption" fontWeight="bold">
                                  {modulo.icono} {modulo.nombre}
                                </Typography>
                                <Tooltip title={isComplete ? "Desactivar todos" : "Activar todos"}>
                                  <IconButton
                                    size="small"
                                    color={isComplete ? "primary" : "default"}
                                    onClick={() => toggleModuloCompleto(propId, modulo.id)}
                                  >
                                    <CheckCircle fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>

                              <Box display="flex" flexDirection="column" gap={0.25}>
                                {modulo.acciones.map((accion) => (
                                  <FormControlLabel
                                    key={accion}
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={acciones[accion] || false}
                                        onChange={() => toggleAccion(propId, modulo.id, accion)}
                                        disabled={accion !== "ver" && !acciones.ver}
                                        sx={{ "&.Mui-checked": { color: COLORS.primary } }}
                                      />
                                    }
                                    label={
                                      <Typography variant="caption">
                                        {ACCIONES_LABELS[accion]}
                                      </Typography>
                                    }
                                    sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.75rem" } }}
                                  />
                                ))}
                              </Box>

                              <Box mt={1}>
                                <Chip
                                  label={`${accionesActivas}/${totalAcciones}`}
                                  size="small"
                                  color={hasPermisosModulo ? "success" : "default"}
                                  sx={{
                                    height: "18px",
                                    "& .MuiChip-label": { fontSize: "0.55rem" },
                                  }}
                                />
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            )}
          </>
        )}
      </Stack>
    );
  };

  const renderFooter = () => {
    if (submitStatus === "success") {
      return (
        <MDButton
          variant="contained"
          color="success"
          onClick={() => onClose(true)}
          startIcon={<CheckCircle />}
          sx={{ px: 4, minWidth: 150, borderRadius: "8px", height: "48px" }}
        >
          Aceptar
        </MDButton>
      );
    }
    if (submitStatus === "error") {
      return (
        <>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
          >
            Cerrar
          </MDButton>
          <MDButton
            variant="contained"
            color="warning"
            onClick={() => {
              setSubmitStatus(null);
              setStatusMessage("");
              setError(null);
            }}
            startIcon={<Refresh />}
            sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
          >
            Reintentar
          </MDButton>
        </>
      );
    }
    return (
      <>
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={handleClose}
          disabled={isLoading}
          sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
        >
          Cancelar
        </MDButton>
        <MDButton
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!selectedUser || isLoading || !tienePermisos}
          startIcon={<Save />}
          sx={{ px: 4, minWidth: 200, borderRadius: "8px", fontWeight: "bold", height: "48px" }}
        >
          {isLoading ? "Guardando..." : usuarioId ? "ACTUALIZAR PERMISOS" : "ASIGNAR PERMISOS"}
        </MDButton>
      </>
    );
  };

  const renderResumen = () => {
    if (!selectedUser || isLoading) return null;
    if (
      selectedPropiedades.length === 0 &&
      !Object.values(permisosGlobalesPropiedades).some((v) => v)
    )
      return null;

    return (
      <MDBox
        sx={{
          p: 3,
          borderRadius: "12px",
          border: "1px solid",
          borderColor: alpha(COLORS.primary, 0.3),
          backgroundColor: alpha(COLORS.primary, 0.02),
        }}
      >
        <Typography variant="h6" fontWeight="600" color="text.primary" gutterBottom>
          RESUMEN DE PERMISOS
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
            >
              USUARIO
            </Typography>
            <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }} noWrap>
              {getUsuarioNombre(Number(selectedUser))}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
            >
              PROPIEDADES (USUARIOS/REGISTROS)
            </Typography>
            <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }} noWrap>
              {selectedPropiedades.length} propiedad(es)
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
            >
              TOTAL DE PERMISOS
            </Typography>
            <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }}>
              {stats.total}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
            >
              PERMISOS ACTIVOS
            </Typography>
            <Typography
              variant="body1"
              fontWeight="600"
              sx={{
                fontSize: "1rem",
                color: stats.activos > 0 ? COLORS.success : "text.secondary",
              }}
            >
              {stats.activos}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
            >
              PROGRESO DE PERMISOS
            </Typography>
            <Typography variant="caption" fontWeight="600" sx={{ fontSize: "0.7rem" }}>
              {progreso}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progreso}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: alpha(COLORS.primary, 0.1),
              "& .MuiLinearProgress-bar": {
                backgroundColor: progreso > 0 ? COLORS.success : COLORS.primary,
                borderRadius: 3,
              },
            }}
          />
        </Box>
        {tienePermisos ? (
          <Box
            sx={{
              p: 1.5,
              borderRadius: "8px",
              backgroundColor: alpha(COLORS.success, 0.05),
              border: "1px dashed",
              borderColor: alpha(COLORS.success, 0.3),
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CheckCircle sx={{ color: COLORS.success, fontSize: 18 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              {stats.activos} permisos activos de {stats.total} disponibles
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              p: 1.5,
              borderRadius: "8px",
              backgroundColor: alpha(COLORS.warning, 0.05),
              border: "1px dashed",
              borderColor: alpha(COLORS.warning, 0.3),
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Warning sx={{ color: COLORS.warning, fontSize: 18 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              Seleccione al menos un permiso para activar
            </Typography>
          </Box>
        )}
      </MDBox>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 3,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box display="flex" alignItems="center">
          {isLoading ? (
            <CircularProgress size={28} sx={{ mr: 2, color: "primary.main" }} />
          ) : submitStatus === "success" ? (
            <CheckCircle sx={{ mr: 2, fontSize: "1.75rem", color: "success.main" }} />
          ) : submitStatus === "error" ? (
            <ErrorIcon sx={{ mr: 2, fontSize: "1.75rem", color: "error.main" }} />
          ) : usuarioId ? (
            <Edit sx={{ mr: 2, fontSize: "1.75rem", color: "primary.main" }} />
          ) : (
            <Add sx={{ mr: 2, fontSize: "1.75rem", color: "primary.main" }} />
          )}
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {titulo}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {usuarioId
                ? `Edite los permisos del usuario seleccionado`
                : "Seleccione un usuario y asigne los permisos"}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: 3 }}>
        {renderContent()}
        {renderResumen()}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, borderTop: 1, borderColor: "divider", gap: 1 }}>
        {renderFooter()}
      </DialogActions>
    </Dialog>
  );
};

ModalPermisos.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onLoadPermisos: PropTypes.func.isRequired,
  usuarios: PropTypes.array,
  propiedades: PropTypes.array,
  permisosData: PropTypes.array,
  usuarioId: PropTypes.number,
  loading: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  titulo: PropTypes.string,
};

ModalPermisos.defaultProps = {
  usuarios: [],
  propiedades: [],
  permisosData: [],
  usuarioId: null,
  loading: false,
  isSubmitting: false,
  titulo: "Asignar Permisos",
};

export default ModalPermisos;
